import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { cloneDeep, get, isEmpty } from 'lodash';
import axios from 'axios';
import { useQuery } from 'react-query';
import { trackQueryError } from '@src/utils/sensor/apptracker';
import { getAlertDiagnosticsBaselineData } from '@src/api/eco2Alerts';
import { setDiagnosticsData } from '@src/store/aiAlerts/diagnostics/actions';
import { changeMode } from '@store/trends/actions';
import { MODE_AI_ALERT_DIAGNOSTICS } from '@src/store/trends/constants';
import {
  getImpactedUniquesValue,
  getLocalTime,
  getPeakSeverity,
  getTotalUniquesValue,
  sortByPriority,
} from '@src/store/aiAlerts/summary/helper';
import moment from 'moment';
import { humanize } from '@src/utils';
import { getAppDefaultGranularity } from '@utils/granularity';
import { getMetricMetaByKey } from '@src/utils/metrics/metricHelpers';
import { MS_TO_SEC_SCALE, PRODUCT_MODE_MAP, ZERO, HUNDRED } from '@src/utils/constants';
import { setDefaultSlice, setSlice } from '@src/store/trends/birdChart/actions';
import {
  getBrushLineDataBySeverity,
  getPcpDropForTooltip,
  getUniqueDevicesForTimeSeries,
  getUniqueDevicesSeries,
} from '@src/store/trends/tempDashboard/aiAlertDiagnostics/utils';
import {
  baselineGraphOption,
  brushLineSeverityData,
  severityBrushLineOption,
  thresholdGraphLineOption,
  tooltipPosition,
  pcpDropYAxis,
  allExceptAnamolyGraphOption,
} from '@src/store/trends/tempDashboard/aiAlertDiagnostics/lineGraphOptions';
import { setSelectedAiAlert } from '@src/store/trends/aiAlerts/actions';
import { updateInterval } from '@src/store/common/actions';
import getMetricsData from '@src/api/metricContainer';

export const useEcoServerAIAlerts = (alertId: string | number, filterToApply) => {
  const dispatch = useDispatch();
  const context = window.conviva.context;
  const { data, isFetching, isError, refetch } = useQuery(
    ['eco-server-ai-alerts-baseline', alertId],
    async () => await getAlertDiagnosticsBaselineData({ params: { alertId, context } }),
    {
      refetchOnWindowFocus: false,
      enabled: alertId !== 'default',
      staleTime: Infinity,
      cacheTime: Infinity,
      onError: (err) => {
        trackQueryError({ type: 'eco-server-ai-alerts-baseline-error', err });
      },
    },
  );

  const alertData = useMemo(() => get(data, 'data', {}), [data]);
  const selectedAlertType = 'ECO_ALERT';

  let selectedAiAlert = {};
  let aiAlertFromDiagnosticDataResponse = {};
  let graphSeriesData = {};

  let startTime = new Date();
  let endTime: string | Date = new Date();
  let sliceStartTime = new Date();
  let sliceEndTime = new Date();

  const diagnosticData = alertData.data ? alertData.data : [];
  startTime = diagnosticData[ZERO] ? diagnosticData[ZERO].timestamp : moment().format();
  endTime = diagnosticData[diagnosticData.length - 1]
    ? moment(diagnosticData[diagnosticData.length - 1].timestamp)
        .add(1, 'minute')
        .format()
    : moment().format();

  const alertMetric = !isEmpty(alertData) && alertData.summary ? alertData.summary.metricKey : null;
  const reqBody = useGetAllExceptAnomalyGraphData(
    alertMetric,
    filterToApply,
    startTime,
    endTime,
    context,
  );

  const source = axios.CancelToken.source();
  // Having the separate query for anomaly data as a dependent query of the alertData query
  const { data: anomalyData } = useQuery(
    ['eco-server-ai-alerts-anomaly'],
    async () => await getMetricsData(reqBody, { cancelToken: source.token }),
    {
      refetchOnWindowFocus: false,
      enabled: alertMetric !== null && alertMetric !== undefined && alertData.data !== undefined,
      //notifyOnChangeProps: ['data', 'error'],
      staleTime: Infinity,
      cacheTime: Infinity,
      onError: (err) => {
        trackQueryError({ type: 'eco-server-ai-alerts-all-except-anomaly-error', err });
      },
    },
  );

  const allExceptAnamolyData = useMemo(() => get(anomalyData, 'data', {}), [anomalyData]);

  // Format and set the alert in the store for metrics chart
  if (!isEmpty(alertData) && alertData.data && alertData.data.length > ZERO) {
    aiAlertFromDiagnosticDataResponse = formatSummaryObject({
      alert: alertData.summary,
      context,
    });
    selectedAiAlert = { ...aiAlertFromDiagnosticDataResponse };
    sliceEndTime = aiAlertFromDiagnosticDataResponse['sliceEndTime'];
    sliceStartTime = aiAlertFromDiagnosticDataResponse['sliceStartTime'];
    selectedAiAlert['startTime'] = startTime;
    selectedAiAlert['endTime'] = endTime;
    selectedAiAlert['diagnosticsData'] = { ...alertData };

    graphSeriesData = getGraphData(selectedAiAlert);
    // setting the graph data for all traffic except the anomaly
    if (
      !isEmpty(allExceptAnamolyData) &&
      allExceptAnamolyData[ZERO] &&
      allExceptAnamolyData[ZERO].data
    ) {
      const aiSeriesData = allExceptAnamolyData[ZERO].data.map((item) => [
        item.timestamp,
        item.results[alertMetric],
      ]);
      graphSeriesData['graphSeries'].push(allExceptAnamolyGraphOption(aiSeriesData));
    }

    selectedAiAlert['graphSeries'] = graphSeriesData['graphSeries'];
    selectedAiAlert['uniqueDevicesSeries'] = graphSeriesData['uniqueDevicesSeries'];
    selectedAiAlert['severityBrushLine'] = graphSeriesData['severityBrushLine'];
    selectedAiAlert['uniqueDevices'] = graphSeriesData['uniqueDevices'];
    selectedAiAlert['pcpDrop'] = graphSeriesData['pcpDrop'];
    selectedAiAlert['pcpDropYAxis'] = pcpDropYAxis;
    dispatch(setDiagnosticsData(alertData));
    dispatch(changeMode(MODE_AI_ALERT_DIAGNOSTICS));
    dispatch(setDefaultSlice([ZERO, HUNDRED], [sliceStartTime, sliceEndTime]));
    dispatch(setSlice([ZERO, HUNDRED], [sliceStartTime, sliceEndTime]));
    dispatch(updateInterval(startTime, endTime, 'custom', 'trends'));
    dispatch(setSelectedAiAlert({ selectedAiAlert, selectedAlertId: alertId, selectedAlertType }));
  }

  return { alertData, isFetching, isError, refetch };
};

const formatSummaryObject = ({ alert, context }) => {
  const { alertEvents, startTime, metricKey, metricName } = alert;
  const isExperienceAlert = context === 'session';
  let formattedAlert = {};
  const clonedAlertEvents = cloneDeep(alertEvents);
  const filteredEvents = clonedAlertEvents.filter((event) => !!event.eventTime);
  const sortedEvents = sortByPriority(filteredEvents);

  const metricInfo = getMetricMetaByKey({
    metricKey: metricKey,
    context: PRODUCT_MODE_MAP.ECO_SERVER,
    defaultValue: {
      displayName: '[DELETED METRIC]',
      unit: '',
    },
  });
  const { unit = '' } = metricInfo;

  // eslint-disable-next-line prefer-spread
  const peakValue = Math.max.apply(
    Math,
    filteredEvents.map((event) => event.value),
  );

  clonedAlertEvents.forEach((event) => {
    const graphTime = moment.utc(event.eventTime * MS_TO_SEC_SCALE).format();
    (event.graphTime = graphTime),
      (event.eventTime = getLocalTime(new Date(event.eventTime * MS_TO_SEC_SCALE)));
    event.peakValue = isExperienceAlert ? `${humanize(peakValue, unit)}${unit}` : 'NA';
    event.peakSeverity = event.alertType;
    event.cumulativeImpactedUniquesRaw = event.cumulativeImpactedUniques;
    event.cumulativeImpactedUniques = event.cumulativeImpactedUniques
      ? humanize(event.cumulativeImpactedUniques, '')
      : 'NA';
  });

  const peakSeverity = isExperienceAlert ? getPeakSeverity(sortedEvents) : null;
  const cumulativeImpactedUniques = isExperienceAlert
    ? getImpactedUniquesValue(sortedEvents)
    : null;
  const cumulativeTotalUniques = isExperienceAlert ? getTotalUniquesValue(sortedEvents) : null;
  const sliceEndTime = new Date(clonedAlertEvents[clonedAlertEvents.length - 1].graphTime);

  const alertMeta = {
    id: alert.alertId,
    pulse5MetricKey: metricKey,
    alertId: alert.alertId,
    metricDisplayName: metricName,
    aiAlertKey: metricKey,
  };

  const alertInfo = {
    peakSeverity,
    alertEvents: isExperienceAlert ? sortedEvents : sortedEvents.slice(-1),
    sliceStartTime: startTime,
    sliceEndTime,
    status: alert.hasEnded ? 'Ended' : 'Active',
    eventTime: getLocalTime(new Date(filteredEvents[ZERO].eventTime * MS_TO_SEC_SCALE)),
    cumulativeImpactedUniquesRaw: cumulativeImpactedUniques,
    cumulativeImpactedUniques: cumulativeImpactedUniques
      ? humanize(getImpactedUniquesValue(sortedEvents), '')
      : 'NA',
    cumulativeTotalUniques: cumulativeTotalUniques
      ? humanize(getTotalUniquesValue(sortedEvents), '')
      : 'NA',
  };
  formattedAlert = { ...alertMeta, ...alertInfo };
  return formattedAlert;
};

// Build the timeseries chart data for the alert diagnostics
const getGraphData = (selectedAiAlert) => {
  const { diagnosticsData, alertEvents, sliceStartTime } = selectedAiAlert;
  const diagnosticsAlertData = diagnosticsData && diagnosticsData.data;
  //currently we only get the threshold, baseline and cummulative devices.
  // Keeping the placeholders for future enhancements
  const thresholdData = [];
  const baselineData = [];
  const projectedData = [];
  const series = [];
  const pcpDropData = [];
  const graphConverter = 1;
  const uniqueDevices = getUniqueDevicesForTimeSeries(diagnosticsAlertData);
  const pcpDrop = getPcpDropForTooltip(diagnosticsAlertData);
  const brushLineDataForSeverity = getBrushLineDataBySeverity(alertEvents);
  if (diagnosticsAlertData.length > 1) {
    diagnosticsAlertData.forEach((currentData) => {
      const threshold = [currentData.timestamp, currentData.results.threshold * graphConverter];
      const baseline = [currentData.timestamp, currentData.results.baseline * graphConverter];
      const pcpDropD = [currentData.timestamp, currentData.results.pcpDrop * graphConverter];
      const projected = [currentData.timestamp, currentData.results.projected * graphConverter];
      thresholdData.push(threshold);
      baselineData.push(baseline);
      pcpDropData.push(pcpDropD);
      projectedData.push(projected);
    });
  }
  brushLineDataForSeverity.unshift(brushLineSeverityData(sliceStartTime));

  series.push(thresholdGraphLineOption(thresholdData));
  series.push(baselineGraphOption(baselineData));

  return {
    tooltipPosition,
    uniqueDevices,
    pcpDrop,
    severityBrushLine: severityBrushLineOption(brushLineDataForSeverity),
    graphSeries: series,
    uniqueDevicesSeries: getUniqueDevicesSeries(diagnosticsAlertData),
  };
};

const useGetAllExceptAnomalyGraphData = (
  pulse5MetricKey,
  filterToApply,
  startTime,
  endTime,
  context,
) => {
  const granularity = getAppDefaultGranularity(startTime, endTime, true, false, null, 'ai-alerts');
  const reqBody: any = {
    queries: [
      {
        dataset: 'ecoserver',
        context: context,
        interval: `${startTime}/${endTime}`,
        granularity: granularity,
        filter: [],
        isLiveMode: false,
        metrics: [pulse5MetricKey],
        type: 'timeseries',
        options: {
          withTotals: true,
        },
      },
    ],
  };
  // all traffic except the rootCause
  const allTrafficExceptAnamoly = filterToApply.rule.map((filter) => {
    const updatedFilter = Object.assign({}, ...filter, { op: 'not_eq' }, { _order: ZERO });
    return updatedFilter;
  });

  // For Eco1 and 2 we set All Traffic as []
  reqBody.queries[ZERO].filter =
    filterToApply.name === 'All Traffic'
      ? // IF server will have a special logic handle OPPOSITE_ALL_TRAFFIC_FOR_AI_ALERT_DIAGNOSTIC
        [
          [
            {
              field: 'app_id',
              op: 'eq',
              value: 'OPPOSITE_ALL_TRAFFIC_FOR_AI_ALERT_DIAGNOSTIC',
              key: '',
              display: 'OPPOSITE_ALL_TRAFFIC_FOR_AI_ALERT_DIAGNOSTIC',
              _order: ZERO,
            },
          ],
        ]
      : [allTrafficExceptAnamoly];

  return reqBody;
};
